import {
  ManagedLeadCategory,
  ManagedLeadField,
  ManagedLeadOwner,
} from '../../services/api/managed-leads/managedLeads';

export const EMPTY_MANAGED_LEAD_VALUE = '—';

export type ManagedLeadDisplayValue =
  | string
  | number
  | boolean
  | null
  | undefined;

const isMissingManagedLeadValue = (value: unknown) =>
  value === null ||
  value === undefined ||
  value === '' ||
  value === EMPTY_MANAGED_LEAD_VALUE;

export const getManagedLeadFieldValue = <T>(
  field: ManagedLeadField<T>
): T | string | number | boolean | null => {
  if (field.state === 'value') return field.value;
  if (
    (field.state === 'unknown' || field.state === 'unknown_applicability') &&
    field.value !== undefined &&
    field.value !== null &&
    field.value !== ''
  ) {
    return field.value;
  }
  return null;
};

export const formatManagedLeadValue = (value: unknown) => {
  if (isMissingManagedLeadValue(value)) {
    return EMPTY_MANAGED_LEAD_VALUE;
  }
  if (typeof value === 'boolean') return value ? 'Sì' : 'No';
  return String(value);
};

/**
 * Names of the three product sections a lead can be listed in. The lists title
 * themselves with these, and an update that moves a lead has to name the same
 * place the operator will look for it.
 */
export const MANAGED_LEAD_CATEGORY_LABELS: Record<ManagedLeadCategory, string> =
  {
    in_progress: 'In gestione',
    won: 'Vinte',
    dropped: 'Drop',
  };

export const UNSUPPORTED_STAGE_MESSAGE =
  'La fase di questa lead non è ancora supportata. Puoi consultarla in sola lettura.';

const STAGE_ALERT_MESSAGES: Record<string, string> = {
  unmapped_active_stage: UNSUPPORTED_STAGE_MESSAGE,
};

/**
 * `stage.alert` carries a stable backend code, never end-user copy: an unknown
 * code still has to reach the user as a sentence instead of a raw identifier.
 */
export const formatManagedLeadStageAlert = (alert: string) =>
  STAGE_ALERT_MESSAGES[alert] ?? UNSUPPORTED_STAGE_MESSAGE;

export const formatManagedLeadField = (field: ManagedLeadField<unknown>) =>
  formatManagedLeadValue(getManagedLeadFieldValue(field));

export const formatManagedLeadOwnerField = (
  field: ManagedLeadField<ManagedLeadOwner>,
  attribute: 'name' | 'email'
) => {
  const owner = getManagedLeadFieldValue(field);
  if (owner === null || typeof owner !== 'object') {
    return EMPTY_MANAGED_LEAD_VALUE;
  }
  return formatManagedLeadValue(owner[attribute]);
};

export const formatManagedLeadCurrency = (value: ManagedLeadDisplayValue) => {
  if (isMissingManagedLeadValue(value)) return EMPTY_MANAGED_LEAD_VALUE;

  const amount = typeof value === 'number' ? value : Number(value);
  if (!Number.isFinite(amount)) return String(value);

  return new Intl.NumberFormat('it-IT', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(amount);
};

export const formatManagedLeadDate = (
  value: ManagedLeadDisplayValue,
  formatter: (value: string | number | Date, format: string) => string
) => {
  if (isMissingManagedLeadValue(value)) return EMPTY_MANAGED_LEAD_VALUE;
  return formatter(String(value), 'dd/MM/yyyy HH:mm');
};
